import React, { useEffect, useState } from 'react';
import { useUser } from '../context/UserContext';
import { useNavigate } from 'react-router-dom';
import GardenProgressTracker from '../components/dashboard/GardenProgressTracker';
import MoodCheckIn from '../components/dashboard/MoodCheckIn';
import DBTPractice from '../components/dashboard/DBTPractice';
import CharacterMessage from '../components/dashboard/CharacterMessage';
import PrincipleLearningCenter from '../components/dashboard/PrincipleLearningCenter';
import PersonalizedPrompts from '../components/dashboard/PersonalizedPrompts';
import { MapPinIcon, ArrowRightIcon, Flower2Icon, LeafIcon } from 'lucide-react';
const DashboardPage: React.FC = () => {
  const {
    userData
  } = useUser();
  const navigate = useNavigate();
  const [highlightedSection, setHighlightedSection] = useState<string | null>(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  // Set highlighted section on initial load
  useEffect(() => {
    // Highlight garden progress initially
    setHighlightedSection('garden');
    // Reset the highlight after 5 seconds
    const timer = setTimeout(() => {
      if (!hasInteracted) {
        setHighlightedSection(null);
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, [hasInteracted]);
  const handleSectionInteraction = (section: string) => {
    setHighlightedSection(section);
    setHasInteracted(true);
    // Clear highlight after 2 seconds
    setTimeout(() => {
      setHighlightedSection(null);
    }, 2000);
  };
  return <div className="py-8 md:py-12 relative" data-id="element-822">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 text-green-200 opacity-30 transform rotate-45" data-id="element-823">
        <Flower2Icon size={120} data-id="element-824" />
      </div>
      <div className="absolute bottom-0 left-0 text-green-200 opacity-30 transform -rotate-15" data-id="element-825">
        <LeafIcon size={100} data-id="element-826" />
      </div>
      <div className="mb-8 relative z-10" data-id="element-827">
        <h1 className="text-3xl font-bold text-green-800 mb-2 flex items-center" data-id="element-828">
          <LeafIcon size={24} className="mr-2 text-green-600" data-id="element-829" />
          Welcome back, {userData.name || 'Explorer'}
        </h1>
        <p className="text-gray-600 pl-9" data-id="element-830">
          Your journey to {userData.goal?.toLowerCase() || 'personal growth'}
        </p>
        <div className="absolute w-32 h-1 bg-gradient-to-r from-green-500 to-transparent rounded mt-2 ml-9" data-id="element-831"></div>
      </div>

      {/* Quest CTA Banner */}
      <div className="mb-8 bg-gradient-to-r from-green-500 to-blue-500 rounded-xl shadow-lg overflow-hidden relative" data-id="element-832">
        <div className="absolute right-0 bottom-0 opacity-20" data-id="element-833">
          <Flower2Icon size={80} className="text-white" data-id="element-834" />
        </div>
        <div className="flex flex-col md:flex-row items-center p-4" data-id="element-835">
          <div className="w-full md:w-1/2 p-4" data-id="element-836">
            <h2 className="text-2xl font-bold text-white mb-2" data-id="element-837">
              Ready for an Adventure?
            </h2>
            <p className="text-white text-opacity-90 mb-4" data-id="element-838">
              Explore quests to restore your sacred gardens and track your
              personal growth journey.
            </p>
            <button onClick={() => navigate('/quests')} className="bg-white text-green-600 py-3 px-6 rounded-lg shadow-md hover:bg-green-50 transition-colors font-medium flex items-center" data-id="element-839">
              Start Your Quest <ArrowRightIcon size={18} className="ml-2" data-id="element-840" />
            </button>
          </div>
          <div className="w-full md:w-1/2 p-4 flex justify-center" data-id="element-841">
            <img src="https://images.unsplash.com/photo-1518531933037-91b2f5f229cc?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80" alt="Garden Quest" className="rounded-lg shadow-md max-h-48 object-cover" data-id="element-842" />
          </div>
        </div>
      </div>

      {/* Decorative divider */}
      <div className="flex items-center justify-center my-8" data-id="element-843">
        <div className="h-px bg-gradient-to-r from-transparent via-green-300 to-transparent w-1/3" data-id="element-844"></div>
        <Flower2Icon size={24} className="mx-4 text-green-500" data-id="element-845" />
        <div className="h-px bg-gradient-to-r from-transparent via-green-300 to-transparent w-1/3" data-id="element-846"></div>
      </div>

      <div className="grid gap-6 md:grid-cols-2" data-id="element-847">
        {/* Garden Progress with highlight */}
        <div className={`transition-all duration-300 transform ${highlightedSection === 'garden' ? 'ring-4 ring-green-400 scale-102 shadow-xl' : 'shadow-md hover:shadow-lg'} h-full`} onClick={() => handleSectionInteraction('garden')} data-id="element-848">
          <GardenProgressTracker data-id="element-849" />
          {highlightedSection === 'garden' && <div className="flex items-center justify-center mt-2 text-green-600" data-id="element-850">
              <MapPinIcon size={16} className="mr-1" data-id="element-851" />
              <span className="text-sm font-medium" data-id="element-852">
                Start here to track your progress
              </span>
            </div>}
        </div>

        {/* Personalized Prompts */}
        <div className={`transition-all duration-300 ${highlightedSection === 'prompts' ? 'ring-4 ring-green-400 scale-102 shadow-xl' : 'shadow-md hover:shadow-lg'} h-full`} onClick={() => handleSectionInteraction('prompts')} data-id="element-853">
          <PersonalizedPrompts data-id="element-854" />
        </div>

        {/* Principle Learning Center */}
        <div className={`transition-all duration-300 ${highlightedSection === 'principles' ? 'ring-4 ring-green-400 scale-102 shadow-xl' : 'shadow-md hover:shadow-lg'} h-full`} onClick={() => handleSectionInteraction('principles')} data-id="element-855">
          <PrincipleLearningCenter data-id="element-856" />
        </div>

        {/* Character Message with highlight option */}
        <div className={`transition-all duration-300 ${highlightedSection === 'character' ? 'ring-4 ring-green-400 scale-102 shadow-xl' : 'shadow-md hover:shadow-lg'} h-full`} onClick={() => handleSectionInteraction('character')} data-id="element-857">
          <CharacterMessage data-id="element-858" />
        </div>

        {/* Mood Check-in with highlight option */}
        <div className={`transition-all duration-300 ${highlightedSection === 'mood' ? 'ring-4 ring-green-400 scale-102 shadow-xl' : 'shadow-md hover:shadow-lg'} h-full`} onClick={() => handleSectionInteraction('mood')} data-id="element-859">
          <MoodCheckIn data-id="element-860" />
        </div>

        {/* DBT Practice with highlight option */}
        <div className={`transition-all duration-300 ${highlightedSection === 'dbt' ? 'ring-4 ring-green-400 scale-102 shadow-xl' : 'shadow-md hover:shadow-lg'} h-full`} onClick={() => handleSectionInteraction('dbt')} data-id="element-861">
          <DBTPractice data-id="element-862" />
        </div>
      </div>

      {/* Bottom decorative element */}
      <div className="flex justify-center mt-12 text-green-400 opacity-50" data-id="element-863">
        <div className="relative" data-id="element-864">
          <Flower2Icon size={32} data-id="element-865" />
          <div className="absolute w-64 h-px bg-gradient-to-r from-green-300 to-transparent top-1/2 -left-64" data-id="element-866"></div>
          <div className="absolute w-64 h-px bg-gradient-to-l from-green-300 to-transparent top-1/2 -right-64" data-id="element-867"></div>
        </div>
      </div>
    </div>;
};
export default DashboardPage;